/* eslint-disable @typescript-eslint/no-explicit-any */
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import { kvGet, kvSet, kvDelete } from './db';
import { enfileirar, usuarioLogado } from './fila';

// Sessão de caixa sem internet. Online, a sessão é sempre a do servidor
// (guardada em cache aqui a cada consulta, ver SessaoCaixaService). Offline,
// o operador pode abrir o caixa mesmo assim: a abertura vira uma sessão
// local (CHAVE_SESSAO_OFFLINE) + um item na fila, e a sincronização chama
// /sessaocaixa/abrir com o mesmo valor informado assim que a conexão
// voltar — antes de enviar as vendas, que o backend vincula à sessão
// aberta do usuário.

const CHAVE_SESSAO_ONLINE = 'sessaoAtiva';
const CHAVE_SESSAO_OFFLINE = 'sessaoOffline';
const CHAVE_CAIXAS = 'caixas';
const chaveStatus = (idconta: string) => `caixaStatus:${idconta}`;

const ehHoje = (data?: string) => !!data && dayjs(data).isSame(dayjs(), 'day');

export const guardarSessaoOnline = (sessao: any | null) =>
  kvSet(CHAVE_SESSAO_ONLINE, { iduser: usuarioLogado().iduser, sessao });

const chaveNomeOperador = () => `nomeOperador:${usuarioLogado().iduser}`;

// O token não traz o nome do usuário — guarda o que o status do caixa
// devolveu online, pra mostrar no rodapé/cupom quando estiver offline.
const nomeOperadorLocal = async () => (await kvGet<string>(chaveNomeOperador())) || usuarioLogado().nome;

export const guardarStatusCaixa = async (status: any) => {
  if (!status?.idconta) return;
  await kvSet(chaveStatus(status.idconta), status);
  if (status.nomeoperador) await kvSet(chaveNomeOperador(), status.nomeoperador);
};

export const guardarCaixas = (caixas: any[]) => kvSet(CHAVE_CAIXAS, caixas);

export const caixasLocais = async (): Promise<any[]> => {
  const caixas = (await kvGet<any[]>(CHAVE_CAIXAS)) || [];
  const sessao = await sessaoOfflinePendente();
  return caixas.map((caixa) => (sessao && sessao.idconta === caixa.idconta
    ? { ...caixa, aberto: true, dataabertura: sessao.dataabertura, valorabertura: sessao.valorabertura, nomeOperador: sessao.nomeUsuario, iduserSessao: sessao.iduser }
    : caixa));
};

// Abertura feita offline e ainda não enviada pro servidor (do usuário logado).
// Só vale no MESMO dia da abertura — igual à sessão do servidor, que vence na
// virada do dia (ver sessaoAindaValidaHoje no backend). Sem isso, uma
// abertura offline de outro dia que nunca subiu deixava o caixa "aberto"
// pra sempre neste navegador (tinha prioridade até sobre o servidor): o PDV
// vendia sem sessão, as vendas voltavam com erro e o "abrir sessão" nunca
// aparecia. O item da fila continua lá pra sincronização (não perde nada).
export const sessaoOfflinePendente = async (): Promise<any | null> => {
  const sessao = await kvGet<any>(CHAVE_SESSAO_OFFLINE);
  if (!sessao || sessao.iduser !== usuarioLogado().iduser) return null;
  if (!ehHoje(sessao.dataabertura)) return null;
  return sessao;
};

export const limparSessaoOffline = () => kvDelete(CHAVE_SESSAO_OFFLINE);

export const sessaoAtivaLocal = async (): Promise<any | null> => {
  const pendente = await sessaoOfflinePendente();
  if (pendente) return pendente;
  const cache = await kvGet<{ iduser: string; sessao: any }>(CHAVE_SESSAO_ONLINE);
  if (cache?.iduser === usuarioLogado().iduser && cache.sessao?.status === 'aberta' && ehHoje(cache.sessao.dataabertura)) {
    return cache.sessao;
  }
  return null;
};

export const abrirSessaoLocal = async (idconta: string, formasPagamento: { valor: number; categoria: string }[]) => {
  const existente = await sessaoAtivaLocal();
  if (existente) return existente;

  const usuario = usuarioLogado();
  const caixas = (await kvGet<any[]>(CHAVE_CAIXAS)) || [];
  const caixa = caixas.find((c) => c.idconta === idconta);
  const idsessao = uuidv4();
  const sessao = {
    idsessao,
    numero: '',
    iduser: usuario.iduser,
    idconta,
    nomeConta: caixa?.titular || '',
    dataabertura: new Date().toISOString(),
    status: 'aberta',
    valorabertura: formasPagamento.reduce((acc, forma) => acc + (Number(forma.valor) || 0), 0),
    nomeUsuario: await nomeOperadorLocal(),
    offline: true,
  };
  await kvSet(CHAVE_SESSAO_OFFLINE, sessao);
  await enfileirar('abrirsessao', idconta, { idconta, formasPagamento }, { dataabertura: sessao.dataabertura, valorabertura: sessao.valorabertura }, idsessao);
  return sessao;
};

const statusDaSessao = (sessao: any, idconta: string) => ({
  idconta,
  nomeoperador: sessao.nomeUsuario || '',
  nomeConta: sessao.nomeConta,
  aberto: true as const,
  dataabertura: sessao.dataabertura,
  valorabertura: sessao.valorabertura ?? 0,
});

// Status do caixa quando existe uma abertura offline ainda não enviada —
// tem prioridade até sobre a resposta do servidor (ver CaixaTurnoService),
// senão o PDV veria o caixa "fechado" no servidor e abriria outra sessão.
export const statusPendenteCaixa = async (idconta: string) => {
  const pendente = await sessaoOfflinePendente();
  return pendente && pendente.idconta === idconta ? statusDaSessao(pendente, idconta) : null;
};

export const statusLocalCaixa = async (idconta: string) => {
  const sessao = await sessaoAtivaLocal();
  if (sessao && sessao.idconta === idconta) return statusDaSessao(sessao, idconta);

  const cache = await kvGet<any>(chaveStatus(idconta));
  if (cache?.aberto && ehHoje(cache.dataabertura)) return cache;

  const caixas = (await kvGet<any[]>(CHAVE_CAIXAS)) || [];
  return {
    idconta,
    nomeoperador: await nomeOperadorLocal(),
    nomeConta: cache?.nomeConta || caixas.find((c) => c.idconta === idconta)?.titular,
    aberto: false as const,
  };
};
